// Dependencies
import React, {
  useState,
  useEffect,
  useCallback,
  ReactNode,
  useRef,
  useTransition,
} from "react";
import { useNavigate } from "react-router";

// MSAL
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "@/config/authConfig";
import { msalInstance } from "@/index";

// Hooks
import { useAuthorization, type UserRole } from "@/hooks/useAuthorization";
import { useCurrentUser } from "@/hooks/useProfileData";
import { useSelectedAccount } from "@/contexts/SelectedAccountContext";
import {
  requiresCompanySetup,
  getIdentityCompanyIdFromClaims,
} from "@/utils/companySetup";
import { IdTokenClaims } from "@/types/auth.types";
import { hasEstablishedDynamicsContact } from "@/utils/dynamicsContact";

// Global Components
import { LoadingIndicator } from "./LoadingIndicator";

// Types
import { loggerService } from "@/services/logger";

interface RouteGuardProps {
  /**
   * List of roles that are allowed to access the route
   * If empty, only authentication is required
   */
  roles?: UserRole[];
  /**
   * If true, the user must be associated with a company (have a ParentOrganization)
   * BusinessAdmin users are exempt from this requirement
   */
  requireCompany?: boolean;
  /**
   * If true, the route requires a persisted CRM contact (not the placeholder / empty GUID profile).
   */
  requireEstablishedContact?: boolean;
  children: ReactNode;
}

export const RouteGuard: React.FC<RouteGuardProps> = ({
  children,
  roles = [],
  requireCompany = false,
  requireEstablishedContact = false,
}) => {
  const normalizeOrganizationId = (id?: string | null) =>
    (id ?? "").trim().replace(/["{}]/g, "").toLowerCase();

  const { inProgress } = useMsal();
  const { getRoles, hasCompany } = useAuthorization();
  const {
    selectedAccountId,
    selectedOrganization,
    setSelectedOrganizationId,
    isLoading: isLoadingSelectedAccount,
  } = useSelectedAccount();
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const hasCheckedAuth = useRef(false);
  const [_isPending, startTransition] = useTransition();

  const currentAccount = msalInstance.getActiveAccount();
  const {
    data: dynamicsContact,
    isLoading: isLoadingProfile,
    error: isProfileError,
  } = useCurrentUser();
  const organizations = dynamicsContact?.organizations ?? [];
  const singleOrganizationAccountId =
    organizations.length === 1 ? organizations[0]?.account?.id : undefined;
  const hasSelectedCompany = organizations.some(
    (organization) =>
      normalizeOrganizationId(organization.account.id) ===
      normalizeOrganizationId(selectedAccountId),
  );
  const effectiveSelectedAccountId =
    selectedAccountId && hasSelectedCompany
      ? selectedAccountId
      : (singleOrganizationAccountId ?? null);

  const checkAuthorization = useCallback((): void => {
    try {
      const userRoles = getRoles();
      let authorized = true;

      // Check role-based authorization if roles are specified
      if (roles.length > 0) {
        const hasRequiredRole = roles.some((role) =>
          userRoles.roles.includes(role),
        );

        if (!hasRequiredRole) {
          authorized = false;
        }
      }

      // Check company requirement if needed
      if (authorized && requireCompany && userRoles.isPersonalUser) {
        if (!hasCompany(dynamicsContact)) {
          const mechanicIds = dynamicsContact?.mechanicIds ?? [];
          const hasMechanicIds = mechanicIds.length > 0;
          const hasMultipleMechanicIds = mechanicIds.length > 1;

          if (hasMultipleMechanicIds) {
            // Redirect to mechanics page if user has multiple mechanic IDs and no company
            if (!window.location.pathname.startsWith("/mechanics")) {
              navigate("/mechanics");
            }
          } else if (hasMechanicIds) {
            // Redirect to home if user has a single mechanic ID and no company
            if (!window.location.pathname.startsWith("/home")) {
              navigate("/home");
            }
          } else {
            // Redirect to home if company is required but not found and no mechanic IDs
            if (!window.location.pathname.startsWith("/home")) {
              navigate("/home");
            }
          }
        } else if (!effectiveSelectedAccountId && !isLoadingSelectedAccount) {
          // Hydrate selection first to avoid refresh-time redirect races.
          const firstOrganizationAccountId = organizations[0]?.account?.id;
          if (firstOrganizationAccountId) {
            setSelectedOrganizationId(firstOrganizationAccountId);
          } else if (!window.location.pathname.startsWith("/company")) {
            navigate("/company");
          }
        }
      }

      const businessIdClaim = currentAccount?.idTokenClaims?.department;
      let businessId =
        typeof businessIdClaim === "string" ? businessIdClaim : undefined;

      // If businessId isn't in the token, try to get it from the contact's first organization
      if (!businessId || businessId === "unknown") {
        businessId = dynamicsContact?.organizations?.[0]?.account?.businessId;
      }

      const canEvaluateCompanySetup = Boolean(dynamicsContact || isProfileError);
      const requiresSetup = canEvaluateCompanySetup
        ? requiresCompanySetup({
            jobTitle: userRoles.jobTitle,
            businessId,
            contactData: dynamicsContact,
          })
        : false;

      if (
        authorized &&
        requireEstablishedContact &&
        dynamicsContact &&
        !hasEstablishedDynamicsContact(dynamicsContact)
      ) {
        if (!window.location.pathname.startsWith("/home")) {
          navigate("/home");
        }
        authorized = false;
      }

      if (authorized && requiresSetup) {
        if (
          !window.location.pathname.startsWith("/company") &&
          !window.location.pathname.startsWith("/paymenthistory")
        ) {
          navigate("/company");
        }
      }

      setIsAuthorized(authorized);
    } catch (error) {
      loggerService.trackException(
        error as Error,
        "Authorization check failed",
      );
      setIsAuthorized(false);
    } finally {
      setIsLoading(false);
    }
  }, [
    getRoles,
    hasCompany,
    navigate,
    roles,
    requireCompany,
    requireEstablishedContact,
    dynamicsContact,
    currentAccount,
    effectiveSelectedAccountId,
    isLoadingSelectedAccount,
  ]);

  useEffect(() => {
    if (!singleOrganizationAccountId) {
      return;
    }

    if (
      normalizeOrganizationId(selectedAccountId) !==
      normalizeOrganizationId(singleOrganizationAccountId)
    ) {
      setSelectedOrganizationId(singleOrganizationAccountId);
    }
  }, [
    selectedAccountId,
    setSelectedOrganizationId,
    singleOrganizationAccountId,
  ]);

  // Handle profile data when it loads
  useEffect(() => {
    if (hasCheckedAuth.current || isLoadingSelectedAccount) return;

    if (dynamicsContact) {
      hasCheckedAuth.current = true;
      startTransition(() => checkAuthorization());
    } else if (isProfileError) {
      hasCheckedAuth.current = true;
      startTransition(() => {
        setIsAuthorized(false);
        setIsLoading(false);
      });
    }
  }, [
    dynamicsContact,
    isProfileError,
    checkAuthorization,
    startTransition,
    isLoadingSelectedAccount,
  ]);

  useEffect(() => {
    if (hasCheckedAuth.current || isLoadingSelectedAccount) return;

    // Log in user if not already logged in
    if (inProgress === InteractionStatus.None) {
      if (!currentAccount) {
        msalInstance.loginRedirect(loginRequest).catch((e) => {
          loggerService.trackException(e as Error, "Login redirect failed");
        });
      } else if (!isLoadingProfile && (dynamicsContact || isProfileError)) {
        // If we've finished loading, check authorization
        // This handles both cases: when we have user data and when we don't
        hasCheckedAuth.current = true;
        startTransition(() => checkAuthorization());
      }
    }
  }, [
    currentAccount,
    inProgress,
    isLoadingProfile,
    dynamicsContact,
    isProfileError,
    checkAuthorization,
    startTransition,
    isLoadingSelectedAccount,
  ]);

  // Handle changes that affect company-setup eligibility.
  useEffect(() => {
    if (isLoadingProfile || isProfileError || !dynamicsContact) {
      return;
    }

    const userRoles = getRoles();
    const identityClaims = currentAccount?.idTokenClaims as
      | IdTokenClaims
      | undefined;
    const businessId = getIdentityCompanyIdFromClaims(identityClaims);

    const requiresSetup = requiresCompanySetup({
      jobTitle: userRoles.jobTitle,
      businessId,
      contactData: dynamicsContact,
    });

    if (
      requiresSetup &&
      !window.location.pathname.startsWith("/company") &&
      !window.location.pathname.startsWith("/paymenthistory")
    ) {
      navigate("/company");
    }
  }, [
    currentAccount,
    dynamicsContact,
    isLoadingProfile,
    isProfileError,
    getRoles,
    navigate,
    selectedOrganization?.id,
    singleOrganizationAccountId,
  ]);

  // Some routes may load contact after first auth check; block placeholder contacts globally
  // when an established contact is required by the route.
  useEffect(() => {
    if (!requireEstablishedContact || !dynamicsContact) {
      return;
    }
    if (hasEstablishedDynamicsContact(dynamicsContact)) {
      return;
    }

    if (!window.location.pathname.startsWith("/home")) {
      navigate("/home");
      // Defer state update to avoid cascading renders (react-compiler / React 19 guidance)
      setTimeout(() => {
        setIsAuthorized(false);
      }, 0);
    }
  }, [requireEstablishedContact, dynamicsContact, navigate]);

  if (isLoading) {
    return <LoadingIndicator />;
  }

  return isAuthorized ? <>{children}</> : null;
};
